import React, { useState } from 'react';
import { Filter, AlertCircle } from 'lucide-react';
import { PetCard } from '../cards/PetCard';
import { usePets } from '../../../hooks/usePets';
import type { User } from '../../../types/user';
import type { PetRarity, PetType } from '../../../types/pets';

interface PetMarketProps {
  user: User | null;
}

export function PetMarket({ user }: PetMarketProps) {
  const { pets, loading, error, refetch } = usePets();
  const [selectedRarity, setSelectedRarity] = useState<PetRarity | 'all'>('all');
  const [selectedType, setSelectedType] = useState<PetType | 'all'>('all');

  const filteredPets = pets.filter(pet => 
    (selectedRarity === 'all' || pet.rarity === selectedRarity) &&
    (selectedType === 'all' || pet.type === selectedType)
  );

  if (loading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-500" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-center">
        <AlertCircle className="w-8 h-8 text-red-500 mx-auto mb-2" />
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={refetch}
          className="px-4 py-2 bg-red-500/20 text-red-400 rounded-lg hover:bg-red-500/30 transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex gap-4 items-center">
        <Filter className="w-5 h-5 text-gray-400" />
        <select
          value={selectedRarity}
          onChange={(e) => setSelectedRarity(e.target.value as PetRarity | 'all')}
          className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg"
        >
          <option value="all">All Rarities</option>
          <option value="common">Common</option>
          <option value="uncommon">Uncommon</option>
          <option value="rare">Rare</option>
          <option value="epic">Epic</option>
          <option value="legendary">Legendary</option>
        </select>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value as PetType | 'all')}
          className="bg-gray-800 text-gray-300 px-3 py-2 rounded-lg"
        >
          <option value="all">All Types</option>
          <option value="dragon">Dragon</option>
          <option value="griffin">Griffin</option>
          <option value="phoenix">Phoenix</option>
          <option value="unicorn">Unicorn</option>
          <option value="wolf">Wolf</option>
        </select>
      </div>

      {filteredPets.length === 0 ? (
        <div className="text-center py-8 text-gray-400">
          No pets found matching your filters
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredPets.map((pet) => (
            <PetCard
              key={pet.id}
              pet={pet}
              user={user}
              onPurchaseComplete={refetch}
            />
          ))}
        </div>
      )}
    </div>
  );
}